import { Children, type ReactNode } from "react";
import { cn } from "../../lib/utils";
import { SINGLE_LINE_BADGE_LABELS } from "./badgeListStyles";
import { TableBadgeListFittedRow } from "./TableBadgeListFittedRow";
import { useTableCellMaxContentHeight, useTableCellOverflow } from "./TableCellContext";
import type { TableColumnOverflow } from "./types";

// In the wrapping modes a badge wider than the column shrinks to it and ellipsizes its label, rather
// than being sliced mid-letter by the cell's clip.
const SHRINKABLE_BADGES = "[&>[data-slot=badge]]:min-w-0 [&>[data-slot=badge]]:max-w-full [&>[data-slot=badge]]:shrink";

const LAYOUT_CLASS: Record<Exclude<TableColumnOverflow, "truncate">, string> = {
  "scroll-horizontal": "flex-nowrap",
  "scroll-vertical": cn("flex-wrap", SHRINKABLE_BADGES),
  expand: cn("flex-wrap", SHRINKABLE_BADGES),
};

export interface TableBadgeListCellProps {
  /**
   * Rendered before the badges (an add button, say) and counted when fitting them to the column.
   * Anything shown beside the list belongs here, or "truncate" would fit badges into space it takes.
   */
  leading?: ReactNode;
  /**
   * One `Badge` per item, its text wrapped in a plain `<span>` (not `.truncate`, which a wrapping
   * column turns into wrapping text) so it can ellipsize on one line.
   */
  children?: ReactNode;
  /** Shown, muted, when there are no badges. */
  emptyLabel: string;
  /**
   * Only for rendering outside a table (a details panel, say). Inside a table the column's own
   * overflow mode applies, so table cells should leave this unset.
   */
  overflow?: TableColumnOverflow;
  className?: string;
}

/**
 * A cell listing badges (tags, care team, journeys, …) that follows its column's overflow mode:
 * "truncate" shows as many badges as fit the column's rendered width at their natural size, then a
 * "+ N more" badge (collapsing to an "N items" badge when none fit), each listing the rest on hover.
 * A row-capped "expand" column fits the same way across the lines its cap allows; uncapped, it wraps
 * every badge. The scroll modes show every badge (on one scrolling line for "scroll-horizontal",
 * wrapped otherwise).
 *
 * @example
 * ```tsx
 * cell: ({ row }) => (
 *   <Table.BadgeListCell emptyLabel={t("No tags")}>
 *     {row.original.tags.map((tag) => (
 *       <Badge key={tag} color="secondary">
 *         <span>{tag}</span>
 *       </Badge>
 *     ))}
 *   </Table.BadgeListCell>
 * )
 * ```
 */
export const TableBadgeListCell = ({ leading, children, emptyLabel, overflow, className }: TableBadgeListCellProps) => {
  const cellOverflow = useTableCellOverflow();
  const maxContentHeight = useTableCellMaxContentHeight();
  const mode = overflow ?? cellOverflow;
  const badges = Children.toArray(children);

  if (badges.length === 0) {
    return (
      <div data-slot="table-badge-list" className={cn("flex min-w-0 items-center gap-1", className)}>
        {leading}
        <span className="truncate text-muted-foreground">{emptyLabel}</span>
      </div>
    );
  }

  if (mode === "truncate") return <TableBadgeListFittedRow badges={badges} leading={leading} className={className} />;
  if (mode === "expand" && maxContentHeight !== undefined) {
    return <TableBadgeListFittedRow badges={badges} leading={leading} multiline className={className} />;
  }

  return (
    <div
      data-slot="table-badge-list"
      className={cn("flex min-w-0 items-center gap-1", LAYOUT_CLASS[mode], SINGLE_LINE_BADGE_LABELS, className)}
    >
      {leading !== undefined && <div className="flex shrink-0 items-center">{leading}</div>}
      {badges}
    </div>
  );
};
